import { Gauge, Cable, CircleDot, Wrench, Disc3, Cog, ScanLine, RotateCcw, Radio, Wind, Droplets, ShieldCheck, Stamp, Phone, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F0EBE0";
const INK = "#1A1F2E";
const INK_SOFT = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const CATEGORIES = [{
  name: "Springs & Tension",
  items: [{
    Icon: Gauge,
    title: "Spring tension test & re-balance",
    detail: "Verify torsion/extension match the door weight."
  }, {
    Icon: Cable,
    title: "Cable inspection",
    detail: "Check for fraying, corrosion, and end-stop wear."
  }, {
    Icon: Disc3,
    title: "Drum & bearing check",
    detail: "Spin-test bearings and tighten cable drums."
  }]
}, {
  name: "Tracks & Rollers",
  items: [{
    Icon: CircleDot,
    title: "Roller condition check",
    detail: "Listen for chatter, replace if pitted or noisy."
  }, {
    Icon: ScanLine,
    title: "Track alignment",
    detail: "Verify plumb, level, and clearance from panels."
  }, {
    Icon: Wrench,
    title: "Hinge inspection & re-torque",
    detail: "Tighten every hinge, replace if rust-bound."
  }]
}, {
  name: "Opener & Safety",
  items: [{
    Icon: Cog,
    title: "Motor function test",
    detail: "Force settings, travel limits, and gear-noise check."
  }, {
    Icon: ShieldCheck,
    title: "Photo-eye / safety reverse",
    detail: "AS/NZS 4505 reversal test with a 50 mm block."
  }, {
    Icon: RotateCcw,
    title: "Manual release (red cord)",
    detail: "Verify the door can be operated by hand in a power-cut."
  }]
}, {
  name: "Comfort & Finish",
  items: [{
    Icon: Radio,
    title: "Remote &amp; keypad function",
    detail: "Range test, battery swap, re-pair if intermittent."
  }, {
    Icon: Wind,
    title: "Weather seal inspection",
    detail: "Bottom astragal, jamb seals — replace if perished."
  }, {
    Icon: Droplets,
    title: "Full lubrication",
    detail: "Springs, hinges, rollers, and tracks — proper grease only."
  }]
}];
export default function ServiceChecklist() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Ambient radial */}
      <div aria-hidden className="absolute inset-0" style={{
      background: `radial-gradient(circle at 50% 0%, ${NAVY_LIGHT}55 0%, ${NAVY} 50%), radial-gradient(circle at 100% 100%, ${GOLD}10 0%, transparent 40%)`
    }} />

      {/* Grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />

      {/* Watermark */}
      <div aria-hidden className="absolute pointer-events-none select-none" style={{
      left: "-30px",
      top: "40px",
      fontFamily: FONT_HEAD,
      fontSize: "240px",
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.05em",
      color: `${GOLD}12`
    }}>
        03
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <div aria-hidden className="h-px w-12" style={{
            backgroundColor: GOLD
          }} />
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: GOLD
          }}>
              03 · The 12-Point Inspection
            </span>
          </div>

          <h2 style={{
          fontFamily: FONT_HEAD,
          fontWeight: 800,
          fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          color: "#ffffff",
          marginBottom: "20px"
        }}>
            What we actually check —
            <br />
            <span style={{
            color: GOLD
          }}>line by line, photographed</span>.
          </h2>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "17px",
          lineHeight: 1.65,
          color: "rgba(255,255,255,0.72)",
          maxWidth: "640px"
        }}>
            Same checklist on every Sydney service call. You get a stamped
            report at the end — pass/flag/fix-now — so you know exactly what's
            healthy and what's wearing.
          </p>
        </div>

        {/* The "report card" — cream document floating on navy */}
        <div className="relative rounded-2xl overflow-hidden" style={{
        backgroundColor: CREAM,
        boxShadow: "0 40px 100px rgba(0,0,0,0.45), 0 0 0 1px rgba(245,166,35,0.2)"
      }}>
          {/* Paper grain */}
          <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
        }} />

          {/* Top gold edge */}
          <div aria-hidden className="absolute top-0 left-0 right-0 h-1.5" style={{
          background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`
        }} />

          {/* Report header — like a real form letterhead */}
          <div className="relative px-7 md:px-10 py-7 flex flex-wrap items-center justify-between gap-4 border-b-2" style={{
          borderColor: NAVY
        }}>
            <div>
              <div style={{
              fontFamily: FONT_HEAD,
              fontSize: "11px",
              fontWeight: 800,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: GOLD_DARK,
              marginBottom: "4px"
            }}>
                Gold Garage Door · Service Record
              </div>
              <h3 style={{
              fontFamily: FONT_HEAD,
              fontWeight: 800,
              fontSize: "clamp(1.2rem, 2vw, 1.6rem)",
              color: NAVY,
              letterSpacing: "-0.02em",
              lineHeight: 1.15
            }}>
                12-Point Annual Inspection Checklist
              </h3>
            </div>

            {/* Stamp */}
            <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg relative" style={{
            border: `2px solid ${GOLD_DARK}`,
            color: GOLD_DARK,
            transform: "rotate(-3deg)"
          }}>
              <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                <Stamp size={16} color={GOLD_DARK} strokeWidth={2.5} />
              </span>
              <span style={{
              fontFamily: FONT_HEAD,
              fontSize: "13px",
              fontWeight: 900,
              letterSpacing: "0.16em",
              textTransform: "uppercase"
            }}>
                Standard on every visit
              </span>
            </div>
          </div>

          {/* Grid of 4 categories × 3 items */}
          <div className="relative grid md:grid-cols-2 lg:grid-cols-4 gap-0">
            {CATEGORIES.map((cat, ci) => <div key={cat.name} className="p-7 md:p-8" style={{
            borderRight: ci < CATEGORIES.length - 1 ? `1px dashed rgba(8,20,56,0.18)` : "none",
            borderBottom: ci < 2 ? `1px dashed rgba(8,20,56,0.18)` : "none"
          }}>
                {/* Category header */}
                <div className="mb-5">
                  <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: GOLD_DARK,
                marginBottom: "4px"
              }}>
                    Section {String(ci + 1).padStart(2, "0")}
                  </div>
                  <h4 style={{
                fontFamily: FONT_HEAD,
                fontWeight: 800,
                fontSize: "1.1rem",
                color: NAVY,
                letterSpacing: "-0.015em",
                lineHeight: 1.2
              }}>
                    {cat.name}
                  </h4>
                </div>

                {/* Items */}
                <ul className="space-y-5">
                  {cat.items.map(({
                Icon,
                title,
                detail
              }, ii) => {
                const itemNo = ci * 3 + ii + 1;
                return <li key={title} className="flex items-start gap-3">
                        {/* Number + checkbox group */}
                        <div className="flex-shrink-0 flex flex-col items-center gap-2">
                          <span style={{
                      fontFamily: FONT_HEAD,
                      fontSize: "10px",
                      fontWeight: 800,
                      color: GOLD_DARK,
                      letterSpacing: "0.05em"
                    }}>
                            {String(itemNo).padStart(2, "0")}
                          </span>
                          <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-md" style={{
                      width: 30,
                      height: 30,
                      backgroundColor: `${GOLD}15`,
                      border: `1px solid ${GOLD}44`
                    }}>
                            <Icon size={15} color={GOLD_DARK} strokeWidth={2.5} />
                          </span>
                        </div>

                        {/* Text */}
                        <div className="flex-1 pt-0.5">
                          <h5 style={{
                      fontFamily: FONT_HEAD,
                      fontWeight: 800,
                      fontSize: "14px",
                      color: NAVY,
                      lineHeight: 1.3,
                      letterSpacing: "-0.01em",
                      marginBottom: "4px"
                    }} dangerouslySetInnerHTML={{
                      __html: title
                    }} />
                          <p style={{
                      fontFamily: FONT_BODY,
                      fontSize: "12.5px",
                      lineHeight: 1.55,
                      color: INK_SOFT
                    }}>
                            {detail}
                          </p>
                        </div>
                      </li>;
              })}
                </ul>
              </div>)}
          </div>

          {/* Report footer */}
          <div className="relative px-7 md:px-10 py-6 flex flex-wrap items-center justify-between gap-4" style={{
          backgroundColor: CREAM_DEEP,
          borderTop: `2px solid ${NAVY}`
        }}>
            <div className="flex items-center gap-3">
              <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                <ShieldCheck size={18} color={GOLD_DARK} strokeWidth={2.5} />
              </span>
              <div>
                <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "13px",
                fontWeight: 800,
                color: NAVY,
                letterSpacing: "-0.01em"
              }}>
                  Stamped, signed &amp; photographed.
                </div>
                <div style={{
                fontFamily: FONT_BODY,
                fontSize: "11.5px",
                color: INK_SOFT
              }}>
                  You keep the report. Insurance &amp; strata love it.
                </div>
              </div>
            </div>

            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "11px",
            fontWeight: 800,
            letterSpacing: "0.22em",
            color: GOLD_DARK
          }}>
              GG · EST 2009
            </span>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "15px",
          lineHeight: 1.6,
          color: "rgba(255,255,255,0.72)",
          maxWidth: "560px"
        }}>
            <span style={{
            color: GOLD,
            fontFamily: FONT_HEAD,
            fontWeight: 700
          }}>
              Every visit. Every door. Every time.
            </span>{" "}
            Same checklist, no shortcuts — that's how we keep the door honest.
          </p>

          <a href="tel:0485000933" className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full transition-all hover:scale-[1.02] flex-shrink-0" style={{
          backgroundColor: GOLD,
          color: NAVY,
          fontFamily: FONT_HEAD,
          fontWeight: 700,
          fontSize: "14px",
          boxShadow: `0 8px 24px ${GOLD}33`
        }}>
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <Phone size={16} strokeWidth={2.5} />
            </span>
            Book my service
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <ArrowRight size={14} strokeWidth={2.5} />
            </span>
          </a>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />
    </section>;
}